import { useState, useEffect } from 'react';
import { DashboardLayout } from '@/components/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '@/components/ui/dialog';
import { Globe, Plus, RefreshCw, Trash2, AlertCircle, Calendar } from 'lucide-react';
import api from '@/lib/api';
import { toast } from 'sonner';
import { localToday } from '@/lib/dateRange';

export default function FXRatesPage() {
  const [rates, setRates] = useState([]);
  const [baseCurrency, setBaseCurrency] = useState('SAR');
  const [activeCurrencies, setActiveCurrencies] = useState([]);
  const [filterCurrency, setFilterCurrency] = useState('');
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  const [addOpen, setAddOpen] = useState(false);
  const [form, setForm] = useState({
    from_currency: '', rate: '', effective_date: localToday(), note: '',
  });

  const fetchData = async () => {
    setLoading(true);
    try {
      const [ratesRes, ccysRes] = await Promise.all([
        api.get('/fx/rates', { params: filterCurrency ? { from_currency: filterCurrency } : {} }),
        api.get('/fx/currencies'),
      ]);
      setRates(ratesRes.data.rates || []);
      setBaseCurrency(ratesRes.data.base_currency || 'SAR');
      setActiveCurrencies(ccysRes.data.currencies || []);
    } catch { toast.error('Failed to load FX rates'); }
    setLoading(false);
  };

  useEffect(() => { fetchData(); }, [filterCurrency]);

  const handleRefresh = async () => {
    setRefreshing(true);
    try {
      const res = await api.post('/fx/refresh');
      if (res.data.error) toast.error(`Refresh failed: ${res.data.error}`);
      else toast.success(`Auto-fetched ${res.data.fetched} rate(s) from exchangerate.host`);
      fetchData();
    } catch (e) { toast.error(e.response?.data?.detail || 'Refresh failed'); }
    setRefreshing(false);
  };

  const saveRate = async () => {
    if (!form.from_currency || !form.rate) { toast.error('Currency + rate required'); return; }
    try {
      await api.post('/fx/rates', { ...form, rate: parseFloat(form.rate) });
      toast.success('Rate saved');
      setAddOpen(false);
      setForm({ from_currency: '', rate: '', effective_date: localToday(), note: '' });
      fetchData();
    } catch (e) { toast.error(e.response?.data?.detail || 'Save failed'); }
  };

  const deleteRate = async (id) => {
    if (!window.confirm('Delete this rate?')) return;
    try { await api.delete(`/fx/rates/${id}`); fetchData(); toast.success('Deleted'); }
    catch { toast.error('Delete failed'); }
  };

  const flag = (code) => {
    if (!code) return '🌍';
    // First 2 letters of currency code usually match country (USD=US, EUR=EU, AED=AE)
    const map = { USD: 'US', EUR: 'EU', GBP: 'GB', AED: 'AE', SAR: 'SA', BHD: 'BH', OMR: 'OM', QAR: 'QA', KWD: 'KW', INR: 'IN', CNY: 'CN', JPY: 'JP', AUD: 'AU', CAD: 'CA', EGP: 'EG', JOD: 'JO', TRY: 'TR', PKR: 'PK', BDT: 'BD', SGD: 'SG', MYR: 'MY', PHP: 'PH', THB: 'TH', IDR: 'ID', ZAR: 'ZA', NGN: 'NG', KES: 'KE', MXN: 'MX', BRL: 'BR', CHF: 'CH' };
    const cc = map[code.toUpperCase()] || code.slice(0, 2).toUpperCase();
    return cc.replace(/./g, c => String.fromCodePoint(127397 + c.charCodeAt(0)));
  };

  return (
    <DashboardLayout>
      <div className="space-y-6 max-w-7xl mx-auto" data-testid="fx-rates-page">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-emerald-500 to-teal-600 flex items-center justify-center shadow-lg">
              <Globe size={22} className="text-white" />
            </div>
            <div>
              <h1 className="text-2xl sm:text-3xl font-bold font-outfit tracking-tight text-foreground">FX Rates</h1>
              <p className="text-sm text-muted-foreground mt-0.5">
                Base: <span className="font-semibold">{baseCurrency}</span> · Active currencies: {activeCurrencies.join(', ')}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="outline" onClick={handleRefresh} disabled={refreshing} data-testid="refresh-fx-btn">
              <RefreshCw size={14} className={`mr-1 ${refreshing ? 'animate-spin' : ''}`} />
              Auto-Fetch (exchangerate.host)
            </Button>
            <Button onClick={() => setAddOpen(true)} data-testid="add-fx-rate-btn"
              className="bg-emerald-500 hover:bg-emerald-600 text-white">
              <Plus size={14} className="mr-1" /> Add Manual Rate
            </Button>
          </div>
        </div>

        <Card>
          <CardHeader className="pb-2 flex flex-row items-center justify-between flex-wrap gap-2">
            <CardTitle className="text-base flex items-center gap-2">
              <Calendar size={14} /> Rate History
            </CardTitle>
            <Select value={filterCurrency || 'all'} onValueChange={(v) => setFilterCurrency(v === 'all' ? '' : v)}>
              <SelectTrigger className="w-[180px] h-8 text-xs" data-testid="fx-currency-filter">
                <SelectValue placeholder="All currencies" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All currencies</SelectItem>
                {activeCurrencies.filter(c => c !== baseCurrency).map(c => (
                  <SelectItem key={c} value={c}>{flag(c)} {c}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </CardHeader>
          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-muted/50 border-b border-border text-xs uppercase text-muted-foreground">
                  <tr>
                    <th className="text-left px-4 py-2 font-semibold">From</th>
                    <th className="text-left px-4 py-2 font-semibold">To (Base)</th>
                    <th className="text-right px-4 py-2 font-semibold">Rate</th>
                    <th className="text-left px-4 py-2 font-semibold">Effective</th>
                    <th className="text-left px-4 py-2 font-semibold">Source</th>
                    <th className="text-left px-4 py-2 font-semibold">Note</th>
                    <th className="w-10"></th>
                  </tr>
                </thead>
                <tbody>
                  {loading ? (
                    <tr><td colSpan={7} className="text-center py-8 text-muted-foreground">Loading...</td></tr>
                  ) : rates.length === 0 ? (
                    <tr><td colSpan={7} className="text-center py-8 text-muted-foreground">
                      No FX rates configured. Use "Auto-Fetch" for today's market rates or "Add Manual Rate" for a specific value.
                    </td></tr>
                  ) : rates.map(r => (
                    <tr key={r.id} className="border-b border-border hover:bg-muted/30" data-testid={`fx-rate-${r.id}`}>
                      <td className="px-4 py-2 font-medium">
                        <span className="mr-1.5">{flag(r.from_currency)}</span>{r.from_currency}
                      </td>
                      <td className="px-4 py-2 text-muted-foreground font-mono">{r.to_currency}</td>
                      <td className="px-4 py-2 text-right font-mono font-semibold text-emerald-700 dark:text-emerald-400">
                        {r.rate.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 6 })}
                      </td>
                      <td className="px-4 py-2 text-muted-foreground font-mono text-xs">{r.effective_date}</td>
                      <td className="px-4 py-2 text-xs">
                        <span className={`px-1.5 py-0.5 rounded font-medium ${
                          r.is_manual ? 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400' : 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400'
                        }`}>
                          {r.is_manual ? 'Manual' : r.source || 'Auto'}
                        </span>
                      </td>
                      <td className="px-4 py-2 text-xs text-muted-foreground">{r.note || '—'}</td>
                      <td className="px-2 py-2">
                        <button onClick={() => deleteRate(r.id)} className="text-muted-foreground hover:text-error">
                          <Trash2 size={14} />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-muted/30">
          <CardContent className="pt-4 flex items-start gap-3">
            <AlertCircle size={18} className="shrink-0 text-info mt-0.5" />
            <div className="text-xs space-y-1">
              <p className="font-semibold text-foreground">How multi-currency works here</p>
              <ul className="list-disc ml-5 space-y-1 text-muted-foreground">
                <li>Each branch's <strong>native currency</strong> is set in <code className="text-foreground">/tax-settings</code> per-branch.</li>
                <li>When a sale / invoice / expense / payment is recorded, the system captures the <strong>native amount</strong>, the <strong>fx_rate to base</strong> (on that transaction's date), and the <strong>base amount</strong>.</li>
                <li><strong>Per-branch reports</strong> (P&L, balance sheet, statements) display in the branch's native currency — <strong>no conversion applied</strong>.</li>
                <li><strong>Tenant-wide / multi-branch reports</strong> consolidate using the stored base_amount — consistent regardless of historical FX.</li>
                <li>Manual rates override auto-fetched rates for the same date.</li>
              </ul>
            </div>
          </CardContent>
        </Card>

        {/* Add rate dialog */}
        <Dialog open={addOpen} onOpenChange={setAddOpen}>
          <DialogContent className="max-w-md">
            <DialogHeader><DialogTitle>Add Manual FX Rate</DialogTitle></DialogHeader>
            <div className="space-y-3 py-2">
              <div>
                <Label className="text-xs">From Currency</Label>
                <Input value={form.from_currency} onChange={(e) => setForm({ ...form, from_currency: e.target.value.toUpperCase() })}
                  placeholder="USD, AED, EUR..." data-testid="fx-from-input" />
              </div>
              <div>
                <Label className="text-xs">Rate (1 {form.from_currency || 'from'} = ? {baseCurrency})</Label>
                <Input type="number" step="0.0001" value={form.rate}
                  onChange={(e) => setForm({ ...form, rate: e.target.value })} data-testid="fx-rate-input" />
              </div>
              <div>
                <Label className="text-xs">Effective Date</Label>
                <Input type="date" value={form.effective_date}
                  onChange={(e) => setForm({ ...form, effective_date: e.target.value })} />
              </div>
              <div>
                <Label className="text-xs">Note (optional)</Label>
                <Input value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })}
                  placeholder="Bank reference, source of rate..." />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setAddOpen(false)}>Cancel</Button>
              <Button onClick={saveRate} data-testid="save-fx-btn"
                className="bg-emerald-500 hover:bg-emerald-600 text-white">Save</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </DashboardLayout>
  );
}
